import { useEventCreateEventTask, useEventDeleteEventTask } from "@crowdcoursing/api/openapi";
import { useCallback } from "react";

import { type Task } from "~ui/molecules/TasksMilestonesCard/TasksMilestonesCard";

import { useToast } from "./useToast";

export interface NewTaskItem {
  name: string;
  description: string;
  dueDate: Date;
  userId?: string;
}

export const useEventTaskManagement = (eventId: string, userId: string, onSuccess?: () => void) => {
  const toast = useToast();

  const { mutateAsync: createTaskMutation, isPending: isCreating } = useEventCreateEventTask();
  const { mutateAsync: deleteTaskMutation, isPending: isDeleting } = useEventDeleteEventTask();

  const createTask = useCallback(
    async (task: NewTaskItem) => {
      await createTaskMutation(
        {
          body: {
            description: task.description,
            dueDate: task.dueDate.toJSON(),
            eventId,
            name: task.name,
            userId: task.userId && task.userId !== "" ? task.userId : userId,
          },
          pathParams: { eventId },
        },
        {
          onError: (error) => {
            console.error("Create task error:", error);
            toast.error("Failed to create task");
          },
          onSuccess: () => {
            toast.success("Task created successfully!");
            onSuccess?.();
          },
        },
      );
    },
    [createTaskMutation, eventId, userId, toast, onSuccess],
  );

  const deleteTask = useCallback(
    async (task: Task) => {
      await deleteTaskMutation(
        {
          pathParams: {
            eventId,
            taskId: task.id,
          },
        },
        {
          onError: (error) => {
            console.error("Delete task error:", error);
            toast.error("Failed to delete task");
          },
          onSuccess: () => {
            toast.success("Task deleted successfully!");
            onSuccess?.();
          },
        },
      );
    },
    [deleteTaskMutation, eventId, toast, onSuccess],
  );

  const completeTask = useCallback(
    async (task: Task) => {
      await deleteTaskMutation(
        {
          pathParams: {
            eventId,
            taskId: task.id,
          },
        },
        {
          onError: (error) => {
            console.error("Complete task error:", error);
            toast.error("Failed to complete task");
          },
          onSuccess: () => {
            toast.success("Task completed!");
            onSuccess?.();
          },
        },
      );
    },
    [deleteTaskMutation, eventId, toast, onSuccess],
  );

  return {
    completeTask,
    createTask,
    deleteTask,
    isCreating,
    isDeleting,
    isLoading: isCreating || isDeleting,
  };
};
